import { initComponentStore, noop } from "@/utils/mobx";
import { BrowserSessionStore } from "@/app_store/agent/BrowserSession.generated";

export type DevtoolsTab = "network" | "console";
export type NetworkFilter = "All" | "XHR" | "JS" | "CSS" | "Img" | "Media" | "Font" | "Doc" | "Other";

export const NETWORK_FILTERS: NetworkFilter[] = ["All", "XHR", "JS", "CSS", "Img", "Media", "Font", "Doc", "Other"];

const _TYPE_MAP: Record<string, NetworkFilter> = {
    XHR: "XHR", Fetch: "XHR",
    Script: "JS",
    Stylesheet: "CSS",
    Image: "Img",
    Media: "Media",
    Font: "Font",
    Document: "Doc",
    Prefetch: "Doc",
};

export function matchesNetworkFilter(resourceType: string, filter: NetworkFilter): boolean {
    if (filter === "All") return true;
    const mapped = _TYPE_MAP[resourceType] ?? "Other";
    return mapped === filter;
}

export interface ConsoleEntry {
    level: string;
    text: string;
}

export interface NetworkRequest {
    requestId: string;
    url: string;
    method: string;
    resourceType: string;
    status: number | null;
    failed: boolean;
    duration: number | null;
}

export class BrowserStreamModalStore {
    isOpen: boolean = false;
    browserSessionId: string = "";
    whepUrl: string | null = null;
    url: string = "";
    session: BrowserSessionStore | null = null;
    proxyViewMode: "desktop" | "mobile" = "desktop";
    onClose: () => any = noop;

    devtoolsOpening: boolean = false;
    devtoolsClosing: boolean = false;
    devtoolsTab: DevtoolsTab = "network";
    devtoolsHeight: number = 220;
    networkFilter: NetworkFilter = "All";

    constructor() {
        initComponentStore(this, {});
    }

    get devtoolsOpen(): boolean {
        if (this.devtoolsClosing) return false;
        return this.devtoolsOpening || (this.session?.devtools_open ?? false);
    }

    get cssCurrentCursor(): string {
        return this.session?.css_cursor ?? "default";
    }

    get consoleEntries(): ConsoleEntry[] {
        return (this.session?.console_log ?? []).map((e: any) => ({
            level: e.level as string,
            text: e.text as string,
        }));
    }

    get networkRequests(): NetworkRequest[] {
        const log = this.session?.network_log ?? [];
        const map = new Map<string, any>();
        const order: string[] = [];
        for (const e of log as any[]) {
            const rid = e.request_id as string;
            if (e.type === "network_request") {
                map.set(rid, {
                    requestId: rid,
                    url: e.url,
                    method: e.method,
                    resourceType: e.resource_type ?? "Other",
                    status: null,
                    failed: false,
                    startTime: e.timestamp as number,
                    endTime: null as number | null,
                });
                order.push(rid);
            } else if (e.type === "network_response" && map.has(rid)) {
                map.get(rid).status = e.status as number;
            } else if (e.type === "network_failed" && map.has(rid)) {
                map.get(rid).failed = true;
                map.get(rid).endTime = e.timestamp as number;
            } else if (e.type === "network_body" && map.has(rid)) {
                map.get(rid).endTime = e.timestamp as number;
            }
        }
        return order.filter(rid => map.has(rid)).map(rid => {
            const r = map.get(rid);
            const dur = r.endTime != null ? r.endTime - r.startTime : null;
            return {
                requestId: r.requestId,
                url: r.url,
                method: r.method,
                resourceType: r.resourceType,
                status: r.status,
                failed: r.failed,
                duration: dur,
            };
        });
    }

    get errorCount(): number {
        return this.consoleEntries.filter(e => e.level === "error" || e.level === "exception").length;
    }

    closeModal() {
        this.isOpen = false;
        this.onClose();
    }

    setProxyViewMode(mode: "desktop" | "mobile") {
        this.proxyViewMode = mode;
    }

    toggleProxyViewMode() {
        this.proxyViewMode = this.proxyViewMode === "desktop" ? "mobile" : "desktop";
    }

    async toggleDevtools() {
        if (this.devtoolsOpen) {
            this.devtoolsClosing = true;
            await this.session?.toggle_devtools();
            this.devtoolsClosing = false;
        } else {
            this.devtoolsOpening = true;
            await this.session?.toggle_devtools();
            this.devtoolsOpening = false;
        }
    }

    setDevtoolsTab(tab: DevtoolsTab) {
        this.devtoolsTab = tab;
    }

    setDevtoolsHeight(h: number) {
        this.devtoolsHeight = Math.max(120, Math.min(600, h));
    }

    setNetworkFilter(f: NetworkFilter) {
        this.networkFilter = f;
    }

    navigate(url: string) {
        void this.session?.navigate(url);
    }

    executeJs(expr: string) {
        void this.session?.execute_js(expr);
    }
}
